import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, TrendingUp, Globe, Search, Database, Lightbulb, RefreshCw, ShieldAlert, Target, BarChart3, Sparkles, CheckCircle2, XCircle, Undo2, History, Building2, Eye, MessageCircle, Hash } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { fetchDashboardStats, fetchDashboardKpis, fetchRecommendations, refreshRecommendations, updateRecommendation, TAG_NAMES, TAG_COLORS, SOCIAL_CAT_COLORS } from "@/lib/api";

const REC_TYPE_CONFIG = {
  trending_opportunity: { label: 'Trending', icon: TrendingUp, color: '#10B981', bg: '#10B98112' },
  competitive_response: { label: 'Competitive', icon: Target, color: '#3B82F6', bg: '#3B82F612' },
  reputation_management: { label: 'Reputation', icon: ShieldAlert, color: '#EF4444', bg: '#EF444412' },
  content_gap: { label: 'Content Gap', icon: BarChart3, color: '#8B5CF6', bg: '#8B5CF612' },
};

const PRIORITY_CONFIG = {
  HIGH: { color: '#EF4444', border: '#EF444440', bg: '#EF444415' },
  MEDIUM: { color: '#F59E0B', border: '#F59E0B40', bg: '#F59E0B15' },
  LOW: { color: '#6B7280', border: '#6B728040', bg: '#6B728015' },
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadStats = useCallback(async () => {
    try {
      const [dashData, kpiData] = await Promise.all([
        fetchDashboardStats(),
        fetchDashboardKpis()
      ]);
      setStats(dashData);
      setKpis(kpiData);
    } catch (e) {
      console.error("Failed to load stats:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [loadStats]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card rounded-none h-32 bg-gray-100 animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-gray-100 animate-pulse rounded-none" />
      </div>
    );
  }

  const catData = (stats?.categories || []).map(c => ({
    name: TAG_NAMES[c.tag] || c.tag,
    tag: c.tag,
    count: c.count,
    fill: TAG_COLORS[c.tag] || '#6B7280'
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="New Competitor Pages" value={kpis?.new_pages_week ?? 0} sub="Last 7 days" color="#3B82F6" testId="stat-new-pages" />
        <StatCard icon={Eye} label="AI Share of Voice" value={`${kpis?.ai_sov ?? 0}%`} sub="JB Hi-Fi on ChatGPT" color="#8B5CF6" isText testId="stat-ai-sov" />
        <StatCard icon={MessageCircle} label="Brand Sentiment" value={`${kpis?.sentiment_score ?? 0}%`} sub={`${kpis?.sentiment_total ?? 0} JB Hi-Fi mentions this week`} color="#10B981" isText testId="stat-sentiment" />
        <StatCard icon={Hash} label="Most Discussed" value={kpis?.most_discussed || '-'} sub={`${kpis?.most_discussed_count ?? 0} mentions this week`} color="#FFEE2F" isText testId="stat-most-discussed" />
      </div>

      {/* Status Bar */}
      <div className="flex items-center gap-4 text-xs font-semibold text-gray-500">
        <div className="flex items-center gap-2">
          <Database className="w-3 h-3 text-gray-400" />
          <span>{stats?.total_social || 0} SOCIAL POSTS TRACKED</span>
        </div>
        <span className="ml-auto">LAST REFRESH {new Date(stats?.last_updated || '').toLocaleTimeString('en-AU')}</span>
      </div>

      {/* AI Recommendations */}
      <RecommendationsSection />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Breakdown Chart */}
        <Card className="glass-card border-gray-200 rounded-none lg:col-span-2" data-testid="category-chart">
          <CardHeader className="pb-2 border-b border-gray-200">
            <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">
              Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {catData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={catData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#6B7280', fontFamily: 'Inter', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fill: '#6B7280', fontFamily: 'Inter', fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 0, fontFamily: 'Inter', fontSize: 12 }} labelStyle={{ color: '#1F2937' }} />
                  <Bar dataKey="count" radius={[0, 2, 2, 0]} cursor="pointer">
                    {catData.map((entry, idx) => (<rect key={idx} fill={entry.fill} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 font-mono-data text-sm">Waiting for events...</div>
            )}
          </CardContent>
        </Card>

        {/* Category Quick Links */}
        <Card className="glass-card border-gray-200 rounded-none" data-testid="category-links">
          <CardHeader className="pb-2 border-b border-gray-200">
            <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">Categories</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-2">
            {Object.entries(TAG_NAMES).map(([tag, name]) => {
              const cat = (stats?.categories || []).find(c => c.tag === tag);
              return (
                <button key={tag} onClick={() => navigate(`/social`)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors duration-200 group" data-testid={`category-link-${tag}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-sm" style={{ background: TAG_COLORS[tag] }} />
                    <span className="font-display text-sm font-semibold text-gray-800 group-hover:text-white">{name}</span>
                  </div>
                  <span className="font-mono-data text-xs text-gray-500">{cat?.count || 0}</span>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Trend Sparklines */}
      {stats?.trend_sparklines?.length > 0 && (
        <Card className="glass-card border-gray-200 rounded-none" data-testid="trend-sparklines">
          <CardHeader className="pb-2 border-b border-gray-200">
            <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">Google Trends - 90 Day Interest</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {stats.trend_sparklines.map((ts) => (
                <div key={ts.category} className="p-3 bg-gray-50 border border-gray-200">
                  <p className="font-display text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{ts.name}</p>
                  {ts.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={48}>
                      <LineChart data={ts.data}>
                        <Line type="monotone" dataKey="value" stroke={TAG_COLORS[ts.category] || '#FFEE2F'} strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-12 flex items-center justify-center text-gray-400 text-[10px] font-mono-data">NO DATA</div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RecommendationsSection() {
  const [recs, setRecs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const data = await fetchRecommendations({});
      setRecs(data.recommendations || []);
      setStats(data.stats || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshRecommendations();
      setTimeout(async () => { await load(); setRefreshing(false); }, 15000);
    } catch (e) { console.error(e); setRefreshing(false); }
  };

  const handleAction = async (recId, action) => {
    try {
      await updateRecommendation(recId, action);
      await load();
    } catch (e) { console.error(e); }
  };

  const generatedAt = recs.find(r => r.status === 'active')?.generated_at || recs[0]?.generated_at;
  const activeRecs = recs.filter(r => r.status === 'active');

  const highRecs = activeRecs.filter(r => r.priority === 'HIGH');
  const medRecs = activeRecs.filter(r => r.priority === 'MEDIUM');
  const lowRecs = activeRecs.filter(r => r.priority === 'LOW');

  return (
    <Card className="glass-card border-gray-200 rounded-none" data-testid="recommendations-section">
      <CardHeader className="pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-display text-sm font-bold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FFEE2F]" />
            AI Recommendations
            {stats && (
              <div className="flex items-center gap-2 ml-2">
                <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-[#FFEE2F]/10 text-[#FFEE2F]">
                  {stats.active} active
                </Badge>
                {stats.actioned > 0 && (
                  <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-emerald-500/10 text-emerald-400">
                    {stats.actioned} done
                  </Badge>
                )}
                {stats.dismissed > 0 && (
                  <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-zinc-500/10 text-gray-500">
                    {stats.dismissed} dismissed
                  </Badge>
                )}
              </div>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {generatedAt && (
              <span className="font-mono-data text-[9px] text-gray-400">
                {new Date(generatedAt).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
              </span>
            )}
            <button
              onClick={() => navigate('/recommendations')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold border border-gray-200 text-gray-500 hover:text-gray-800 transition-all"
              data-testid="show-all-recommendations"
            >
              <History className="w-3 h-3" />
              Show All
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold border border-gray-200 text-gray-600 hover:text-white hover:border-gray-300 transition-all disabled:opacity-50"
              data-testid="refresh-recommendations"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Analyzing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 animate-pulse" />)}</div>
        ) : activeRecs.length === 0 && !showResolved ? (
          <div className="py-10 text-center">
            <Lightbulb className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="font-mono-data text-sm text-gray-400">
              {stats?.total > 0 ? 'All recommendations handled!' : 'No recommendations yet'}
            </p>
            <p className="font-mono-data text-xs text-gray-400 mt-1">
              {stats?.total > 0 ? `${stats.actioned} actioned, ${stats.dismissed} dismissed` : 'Recommendations generate after social data is collected'}
            </p>
            <button onClick={handleRefresh} disabled={refreshing}
              className="mt-4 px-4 py-2 text-xs font-display uppercase tracking-wider border border-[#FFEE2F]/30 text-[#FFEE2F] hover:bg-[#FFEE2F]/10 transition-all disabled:opacity-50"
              data-testid="generate-first-recommendations"
            >
              {refreshing ? 'Generating...' : 'Generate New'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Active recommendations grouped by priority */}
            {[highRecs, medRecs, lowRecs].filter(g => g.length > 0).map((group) => {
              const priority = group[0]?.priority || 'LOW';
              const pCfg = PRIORITY_CONFIG[priority];
              return (
                <div key={priority}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-display text-[10px] font-bold uppercase tracking-widest" style={{ color: pCfg.color }}>{priority} PRIORITY</span>
                    <div className="flex-1 h-px" style={{ background: `${pCfg.color}20` }} />
                  </div>
                  <div className="space-y-2">
                    {group.map((rec, i) => (
                      <RecommendationCard key={rec.rec_id || `${priority}-${i}`} rec={rec} onAction={handleAction} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecommendationCard({ rec, onAction, resolved }) {
  const typeConfig = REC_TYPE_CONFIG[rec.type] || REC_TYPE_CONFIG.content_gap;
  const TypeIcon = typeConfig.icon;
  const pCfg = PRIORITY_CONFIG[rec.priority] || PRIORITY_CONFIG.LOW;
  const catColor = SOCIAL_CAT_COLORS[rec.category?.toLowerCase()] || TAG_COLORS[rec.category?.toLowerCase()] || '#6B7280';
  const isActioned = rec.status === 'actioned';
  const isDismissed = rec.status === 'dismissed';

  return (
    <div
      className={`p-4 border transition-all duration-200 group ${
        resolved ? 'bg-white/20 border-white/[0.02] opacity-60 hover:opacity-80' : 'bg-white/30 border-white/[0.04] hover:bg-gray-100'
      }`}
      style={{ borderLeft: `3px solid ${resolved ? (isActioned ? '#10B981' : '#52525b') : pCfg.color}` }}
      data-testid={`recommendation-${rec.rec_id || ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center mt-0.5" style={{ background: resolved ? '#27272a' : typeConfig.bg }}>
          {isActioned ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> :
           isDismissed ? <XCircle className="w-4 h-4 text-gray-400" /> :
           <TypeIcon className="w-4 h-4" style={{ color: typeConfig.color }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className={`font-display text-sm font-bold ${resolved ? 'text-gray-500 line-through decoration-zinc-700' : 'text-white'}`}>{rec.title}</h4>
            <span className="text-[9px] font-mono-data px-1.5 py-0" style={{ color: typeConfig.color, background: typeConfig.bg, border: `1px solid ${typeConfig.color}30` }}>
              {typeConfig.label}
            </span>
            {rec.category && (
              <span className="text-[9px] font-mono-data px-1.5 py-0" style={{ color: catColor, background: `${catColor}12`, border: `1px solid ${catColor}25` }}>
                {rec.category}
              </span>
            )}
            {isActioned && <span className="text-[9px] font-mono-data px-1.5 py-0 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Actioned</span>}
            {isDismissed && <span className="text-[9px] font-mono-data px-1.5 py-0 bg-gray-200 text-gray-400 border border-gray-300/30">Dismissed</span>}
          </div>
          <p className={`font-mono-data text-xs leading-relaxed ${resolved ? 'text-gray-400' : 'text-gray-600'}`}>{rec.description}</p>
          {rec.data_signal && (
            <p className="font-mono-data text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
              <Activity className="w-2.5 h-2.5" /> Signal: {rec.data_signal}
            </p>
          )}
          {rec.resolved_at && (
            <p className="font-mono-data text-[9px] text-gray-400 mt-1">
              {isActioned ? 'Completed' : 'Dismissed'} {new Date(rec.resolved_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        {/* Action buttons */}
        <div className="flex-shrink-0 flex flex-col gap-1.5 ml-2">
          {!resolved ? (
            <>
              <button
                onClick={() => onAction(rec.rec_id, 'actioned')}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display uppercase tracking-wider font-semibold border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10 transition-all"
                title="Mark as actioned"
                data-testid={`action-done-${rec.rec_id}`}
              >
                <CheckCircle2 className="w-3 h-3" /> Done
              </button>
              <button
                onClick={() => onAction(rec.rec_id, 'dismissed')}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display uppercase tracking-wider font-semibold border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-all"
                title="Dismiss"
                data-testid={`action-dismiss-${rec.rec_id}`}
              >
                <XCircle className="w-3 h-3" /> Skip
              </button>
            </>
          ) : (
            <button
              onClick={() => onAction(rec.rec_id, 'active')}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display uppercase tracking-wider font-semibold border border-gray-300 text-gray-400 hover:text-gray-600 transition-all"
              title="Restore to active"
              data-testid={`action-restore-${rec.rec_id}`}
            >
              <Undo2 className="w-3 h-3" /> Restore
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, isText, testId }) {
  return (
    <div className="bg-white border border-gray-200 rounded-none animate-fade-in-up p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className={isText ? "text-xl font-black text-gray-900 truncate" : "text-3xl font-black text-gray-900"}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <p className="text-[10px] font-semibold text-gray-400 mt-2 tracking-wider">{sub}</p>
    </div>
  );
}
